import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Printer, QrCode, Tag, Plus, Trash2, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { QRCodeSVG } from "qrcode.react";
import { useReactToPrint } from "react-to-print";

interface LabelData {
  id: string;
  workOrder: string;
  client: string;
  projectName: string;
  material: string;
  process: string;
  operator: string;
  date: string;
  pieceNumber: string;
  totalPieces: string;
  notes: string;
}

type LabelSize = "small" | "medium" | "large";

const LABEL_SIZES = {
  small: { width: "5cm", height: "3cm", name: "Pequeña (5x3 cm)", qrSize: 50, fontSize: "6px" },
  medium: { width: "10cm", height: "5cm", name: "Mediana (10x5 cm)", qrSize: 80, fontSize: "9px" },
  large: { width: "15cm", height: "10cm", name: "Grande (15x10 cm)", qrSize: 120, fontSize: "12px" },
};

const generateId = () => Math.random().toString(36).substr(2, 9);

const getCurrentDate = () => {
  const now = new Date();
  return now.toISOString().split('T')[0];
};

export default function Labels() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const printRef = useRef<HTMLDivElement>(null);

  const [labelSize, setLabelSize] = useState<LabelSize>("medium");
  const [labels, setLabels] = useState<LabelData[]>([{
    id: generateId(),
    workOrder: "",
    client: "",
    projectName: "",
    material: "",
    process: "",
    operator: "",
    date: getCurrentDate(),
    pieceNumber: "1",
    totalPieces: "1",
    notes: "",
  }]);
  const [copies, setCopies] = useState(1);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Etiquetas-${labels[0]?.workOrder || 'Laminado'}`,
  });

  const updateLabel = (index: number, field: keyof LabelData, value: string) => {
    const newLabels = [...labels];
    newLabels[index] = { ...newLabels[index], [field]: value };
    setLabels(newLabels);
  };

  const addLabel = () => {
    const lastLabel = labels[labels.length - 1];
    const newPieceNumber = parseInt(lastLabel.pieceNumber || "0") + 1;
    setLabels([...labels, {
      ...lastLabel,
      id: generateId(),
      pieceNumber: newPieceNumber.toString(),
    }]);
  };

  const removeLabel = (index: number) => {
    if (labels.length > 1) {
      setLabels(labels.filter((_, i) => i !== index));
    }
  };

  const generateQRData = (label: LabelData) => {
    return JSON.stringify({
      wo: label.workOrder,
      cl: label.client,
      pj: label.projectName,
      mt: label.material,
      pr: label.process,
      op: label.operator,
      dt: label.date,
      pc: `${label.pieceNumber}/${label.totalPieces}`,
    });
  };

  const size = LABEL_SIZES[labelSize];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {language === "es" ? "Volver" : "Back"}
            </Button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
                <Tag className="w-8 h-8 text-primary" />
                {language === "es" ? "Etiquetas de Laminado" : "Lamination Labels"}
              </h1>
              <p className="text-muted-foreground text-sm">
                {language === "es"
                  ? "Genera e imprime etiquetas con código QR para tus piezas"
                  : "Generate and print QR code labels for your pieces"}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={addLabel}>
              <Plus className="w-4 h-4 mr-2" />
              {language === "es" ? "Añadir etiqueta" : "Add label"}
            </Button>
            <Button onClick={() => handlePrint()}>
              <Printer className="w-4 h-4 mr-2" />
              {language === "es" ? "Imprimir" : "Print"}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Formulario */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {language === "es" ? "Configuración" : "Settings"}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{language === "es" ? "Tamaño de etiqueta" : "Label size"}</Label>
                    <Select value={labelSize} onValueChange={(v) => setLabelSize(v as LabelSize)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="small">Pequeña (5x3 cm)</SelectItem>
                        <SelectItem value="medium">Mediana (10x5 cm)</SelectItem>
                        <SelectItem value="large">Grande (15x10 cm)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{language === "es" ? "Copias por etiqueta" : "Copies per label"}</Label>
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      value={copies}
                      onChange={(e) => setCopies(parseInt(e.target.value) || 1)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {labels.map((label, index) => (
              <Card key={label.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">
                      {language === "es" ? `Etiqueta ${index + 1}` : `Label ${index + 1}`}
                    </CardTitle>
                    {labels.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeLabel(index)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{language === "es" ? "Orden de Trabajo" : "Work Order"} *</Label>
                      <Input
                        placeholder="OT-2024-001"
                        value={label.workOrder}
                        onChange={(e) => updateLabel(index, "workOrder", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{language === "es" ? "Cliente" : "Client"}</Label>
                      <Input
                        placeholder="Nombre del cliente"
                        value={label.client}
                        onChange={(e) => updateLabel(index, "client", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{language === "es" ? "Proyecto / Pieza" : "Project / Piece"}</Label>
                    <Input
                      placeholder="Nombre del proyecto o pieza"
                      value={label.projectName}
                      onChange={(e) => updateLabel(index, "projectName", e.target.value)}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{language === "es" ? "Material" : "Material"}</Label>
                      <Input
                        placeholder="Fibra de carbono, etc."
                        value={label.material}
                        onChange={(e) => updateLabel(index, "material", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{language === "es" ? "Proceso" : "Process"}</Label>
                      <Input
                        placeholder="Laminado, Curado, etc."
                        value={label.process}
                        onChange={(e) => updateLabel(index, "process", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{language === "es" ? "Operario" : "Operator"}</Label>
                      <Input
                        placeholder="Nombre del operario"
                        value={label.operator}
                        onChange={(e) => updateLabel(index, "operator", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{language === "es" ? "Fecha" : "Date"}</Label>
                      <Input
                        type="date"
                        value={label.date}
                        onChange={(e) => updateLabel(index, "date", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{language === "es" ? "Pieza Nº" : "Piece #"}</Label>
                      <Input
                        type="number"
                        min="1"
                        value={label.pieceNumber}
                        onChange={(e) => updateLabel(index, "pieceNumber", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{language === "es" ? "Total Piezas" : "Total Pieces"}</Label>
                      <Input
                        type="number"
                        min="1"
                        value={label.totalPieces}
                        onChange={(e) => updateLabel(index, "totalPieces", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{language === "es" ? "Notas" : "Notes"}</Label>
                    <Textarea
                      placeholder={language === "es" ? "Observaciones adicionales..." : "Additional notes..."}
                      value={label.notes}
                      onChange={(e) => updateLabel(index, "notes", e.target.value)}
                      rows={2}
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Vista previa */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <QrCode className="w-5 h-5" />
                  {language === "es" ? "Vista Previa" : "Preview"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 overflow-auto max-h-[70vh]">
                  <div ref={printRef} className="space-y-4">
                    {labels.map((label) => (
                      Array.from({ length: copies }).map((_, copyIndex) => (
                        <div
                          key={`${label.id}-${copyIndex}`}
                          className="bg-white border-2 border-dashed border-gray-300 rounded-lg p-3 mx-auto page-break-inside-avoid"
                          style={{
                            width: size.width,
                            minHeight: size.height,
                            fontSize: size.fontSize,
                          }}
                        >
                          {/* Header con logo */}
                          <div className="flex justify-between items-start mb-2 border-b pb-1">
                            <div>
                              <div className="font-bold text-primary" style={{ fontSize: `calc(${size.fontSize} * 1.4)` }}>
                                MAGMA COMPOSITES
                              </div>
                              <div className="text-gray-500" style={{ fontSize: `calc(${size.fontSize} * 0.9)` }}>
                                Etiqueta de Laminado
                              </div>
                            </div>
                            <div className="text-right text-gray-500" style={{ fontSize: `calc(${size.fontSize} * 0.85)` }}>
                              {label.date}
                            </div>
                          </div>

                          {/* Contenido */}
                          <div className="flex gap-2">
                            {/* QR Code */}
                            <div className="flex-shrink-0">
                              <QRCodeSVG
                                value={generateQRData(label)}
                                size={size.qrSize}
                                level="M"
                                includeMargin={false}
                              />
                            </div>

                            {/* Información */}
                            <div className="flex-1 space-y-1">
                              <div className="font-bold text-primary" style={{ fontSize: `calc(${size.fontSize} * 1.3)` }}>
                                {label.workOrder || "---"}
                              </div>

                              {label.client && (
                                <div><span className="font-semibold">Cliente:</span> {label.client}</div>
                              )}

                              {label.projectName && (
                                <div><span className="font-semibold">Proyecto:</span> {label.projectName}</div>
                              )}

                              {label.material && (
                                <div><span className="font-semibold">Material:</span> {label.material}</div>
                              )}

                              {label.process && (
                                <div><span className="font-semibold">Proceso:</span> {label.process}</div>
                              )}

                              {label.operator && (
                                <div><span className="font-semibold">Operario:</span> {label.operator}</div>
                              )}

                              {/* Número de pieza */}
                              <div className="font-bold mt-1 pt-1 border-t" style={{ fontSize: `calc(${size.fontSize} * 1.2)` }}>
                                Pieza: {label.pieceNumber} / {label.totalPieces}
                              </div>

                              {label.notes && labelSize !== "small" && (
                                <div className="text-gray-500 italic mt-1" style={{ fontSize: `calc(${size.fontSize} * 0.85)` }}>
                                  {label.notes}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Instrucciones */}
            <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200">
              <CardContent className="pt-4">
                <h4 className="font-semibold text-blue-700 dark:text-blue-300 mb-2">
                  {language === "es" ? "💡 Consejos de impresión" : "💡 Printing tips"}
                </h4>
                <ul className="text-sm text-blue-600 dark:text-blue-400 space-y-1">
                  <li>• {language === "es" ? "Usa papel adhesivo para etiquetas" : "Use adhesive label paper"}</li>
                  <li>• {language === "es" ? "Configura la impresora sin márgenes" : "Set printer to borderless"}</li>
                  <li>• {language === "es" ? "Escoge 'Ajustar a página' en opciones de impresión" : "Choose 'Fit to page' in print options"}</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Estilos de impresión */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #print-area, #print-area * {
            visibility: visible;
          }
          .page-break-inside-avoid {
            page-break-inside: avoid;
          }
        }
      `}</style>
    </div>
  );
}
